import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DropdownValue } from '../types';

const EMPTY_DROPDOWN_VALUES: DropdownValue[] = [];

type UseDropdownExpansionResult = {
  expandedSet: Set<DropdownValue>;
  toggleExpanded: (value: DropdownValue) => void;
};

export function useDropdownExpansion(
  expandable: boolean,
  defaultExpandedValues: DropdownValue[] = EMPTY_DROPDOWN_VALUES,
  search?: { term: string; expandableValues: DropdownValue[] },
): UseDropdownExpansionResult {
  const hasUserToggledRef = useRef(false);
  const [expandedValues, setExpandedValues] = useState<DropdownValue[]>(defaultExpandedValues);
  const searchTerm = search?.term ?? '';
  const [searchExpansion, setSearchExpansion] = useState({
    term: searchTerm,
    toggledValues: EMPTY_DROPDOWN_VALUES,
  });

  if (searchExpansion.term !== searchTerm) {
    setSearchExpansion({ term: searchTerm, toggledValues: EMPTY_DROPDOWN_VALUES });
  }

  const expandedSet = useMemo(() => {
    if (!searchTerm) {
      return new Set(expandedValues);
    }

    const next = new Set(search?.expandableValues);

    if (searchExpansion.term === searchTerm) {
      for (const value of searchExpansion.toggledValues) {
        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }
      }
    }

    return next;
  }, [expandedValues, search?.expandableValues, searchExpansion, searchTerm]);

  useEffect(() => {
    if (!expandable) {
      hasUserToggledRef.current = false;
      setExpandedValues(EMPTY_DROPDOWN_VALUES);

      return;
    }

    if (!hasUserToggledRef.current) {
      setExpandedValues(defaultExpandedValues);
    }
  }, [defaultExpandedValues, expandable]);

  const toggleExpanded = useCallback(
    (value: DropdownValue) => {
      if (!expandable) {
        return;
      }

      if (searchTerm) {
        setSearchExpansion((previous) => ({
          term: searchTerm,
          toggledValues: previous.toggledValues.includes(value)
            ? previous.toggledValues.filter((toggledValue) => toggledValue !== value)
            : [...previous.toggledValues, value],
        }));

        return;
      }

      hasUserToggledRef.current = true;

      setExpandedValues((previous) => {
        const next = new Set(previous);

        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }

        return [...next];
      });
    },
    [expandable, searchTerm],
  );

  return {
    expandedSet,
    toggleExpanded,
  };
}
